import type { JSX } from 'react';
import { useState } from 'react';
import { Button } from '@/components/ui/button.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.js';
import { Field } from '@/components/ui/field.js';
import { Input } from '@/components/ui/input.js';

export interface IgnoreReasonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => void;
  isConfirming?: boolean;
  /** Précision du contenu concerné (numéro de commande, ou nombre de commandes sélectionnées). */
  description: string;
}

/**
 * « Marquer ignorée » (`01` §4) : motif obligatoire, partagé par l'action de
 * ligne (`order-row-actions.tsx`) et l'action groupée (`bulk-action-bar.tsx`).
 */
export function IgnoreReasonDialog({
  open,
  onOpenChange,
  onConfirm,
  isConfirming = false,
  description,
}: IgnoreReasonDialogProps): JSX.Element {
  const [reason, setReason] = useState('');

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setReason('');
        }
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Marquer ignorée</DialogTitle>
          <DialogDescription>
            {description} Aucun envoi ne sera effectué. Le motif est obligatoire.
          </DialogDescription>
        </DialogHeader>
        <Field label="Motif" htmlFor="ignore-reason">
          <Input
            id="ignore-reason"
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
            }}
            placeholder="Annulée, remboursée, déjà expédiée…"
            autoFocus
          />
        </Field>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isConfirming}
          >
            Annuler
          </Button>
          <Button
            type="button"
            variant="destructive"
            isLoading={isConfirming}
            disabled={reason.trim() === ''}
            onClick={() => {
              onConfirm(reason.trim());
            }}
          >
            Marquer ignorée
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
